import dayjs from 'dayjs'
import { ViewFeeModel } from 'src/clinic/views/clinicBrandPayoutReports/components/FeeModelViewDetails'
import { isValidNumber } from 'src/consumer/helpers/utilFunctions'
import { MOMENT_FORMATS } from 'src/consumer/constants'
import { getPriceStringWithCurrency } from 'src/mentor/helpers/utilityFunctions'

export default function FeeModelViewer({
  entityFeeModel = {},
  allFeeModels,
  modelNo,
  feeModelGroups,
  applicableToObject: applicableToObjectOverride,
  forMentor = false,
  isPlaceholder = false,
  isInactive = false,
}) {
  let applicableToObject =
    applicableToObjectOverride ||
    entityFeeModel?.applicableToObject ||
    feeModelGroups?.find((group) => group?.id === entityFeeModel?.applicableTo)

  const title1 =
    applicableToObject?.titleForEntityFeeModel ||
    applicableToObject?.titleForEntityModel ||
    applicableToObject?.title ||
    entityFeeModel?.applicableTo ||
    'Engagement Model'
  let title2 = forMentor ? 'Both In Person and Online Sessions' : 'Both in Clinic and Online Sessions'

  let sessionTitle = 'Sessions'
  let modeTitle = forMentor
    ? `All ${sessionTitle} both in Person & Online in Home`
    : `All ${sessionTitle} both in Clinic & Online in Home`

  if (entityFeeModel?.mode === 'online') {
    title2 = 'Online Sessions'
    modeTitle = `Online ${sessionTitle} from Home`
  }
  if (entityFeeModel?.mode === 'offline') {
    title2 = forMentor ? 'In Person Sessions' : 'In Clinic Sessions'
    modeTitle = forMentor ? `In Person ${sessionTitle}` : `In Clinic ${sessionTitle}`
  }

  const currency = entityFeeModel?.currencyCode
  const minimumFeePerSession = entityFeeModel?.minimumFeePerSession

  const minFeeTitle = `Min Fee from Patient is to be ${getPriceStringWithCurrency(
    minimumFeePerSession,
    currency,
  )} for each session`

  const minFeeTitle1 =
    'AUMHUM revenue share will be calculated on minimum fee if fee collected is less than minimum fee'

  const headingPrefix = modelNo ? `${modelNo}. ` : ''
  const headingSuffix = isPlaceholder ? '(Not Active)' : `(${title2})`

  return (
    <div className="d-flex flex-column gap-1">
      <div className="d-flex flex-column gap-1 mb-3">
        <h4 className="mb-0">
          {headingPrefix}
          {title1} {headingSuffix}
        </h4>
        {!!applicableToObject?.longDesc && <span>{applicableToObject?.longDesc}</span>}
        {isPlaceholder ? (
          <span style={{ color: '#6c757d' }}>
            No active engagement model is configured for this bucket right now.
          </span>
        ) : (
          <ValidityDisplay entityFeeModel={entityFeeModel} isInactive={isInactive} />
        )}
      </div>
      {!isPlaceholder && (
        <>
          <span>
            <strong>Mode :</strong> {modeTitle}
          </span>
          {isValidNumber(minimumFeePerSession) && (
            <>
              <span>
                <strong>Min Fee :</strong> {minFeeTitle}
              </span>
              <span style={{ textDecoration: 'underline' }}>
                <strong>{minFeeTitle1}</strong>
              </span>
            </>
          )}
          <div className="d-flex flex-column gap-3 mt-2">
            {entityFeeModel?.feeRanges?.map((range, idx) => {
              return (
                <EntityFeeModelPriceRangeViewer
                  key={idx}
                  minFee={range?.min}
                  maxFee={range?.max}
                  currencyCode={currency}
                  modelId={range?.modelId}
                  allFeeModels={allFeeModels}
                  forMentor={forMentor}
                />
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

function EntityFeeModelPriceRangeViewer({
  minFee,
  maxFee,
  currencyCode,
  modelId,
  allFeeModels,
  forMentor,
}) {
  const feeModel = allFeeModels?.find((model) => model?.id === modelId)

  let mainTitle = `For Fee Upto ${
    isValidNumber(maxFee) ? getPriceStringWithCurrency(maxFee, currencyCode) : 0
  }`

  if (isValidNumber(minFee) && isValidNumber(maxFee) && minFee !== 0) {
    mainTitle = `For Fee between ${
      isValidNumber(minFee) ? getPriceStringWithCurrency(minFee, currencyCode) : 0
    } - ${isValidNumber(maxFee) ? getPriceStringWithCurrency(maxFee, currencyCode) : 0}`
  }
  if (isValidNumber(minFee) && !isValidNumber(maxFee)) {
    if (minFee === 0) {
      mainTitle = `For any amount collected`
    } else {
      mainTitle = `For fee ${
        isValidNumber(minFee) ? getPriceStringWithCurrency(minFee, currencyCode) : 0
      } and above`
    }
  }

  return (
    <div
      className="d-flex flex-column gap-1"
      style={{ background: '#dbdbdb', borderRadius: '8px', padding: '16px' }}
    >
      <strong>{mainTitle}</strong>
      {feeModel ? (
        <ViewFeeModel hideHeading feeModel={feeModel} forMentor={forMentor} />
      ) : (
        <span style={{ color: '#6c757d' }}>Fee model details are unavailable.</span>
      )}
    </div>
  )
}

function ValidityDisplay({ entityFeeModel, isInactive = false }) {
  const applicableFrom = entityFeeModel?.applicableFrom ? dayjs.utc(entityFeeModel.applicableFrom).local() : null
  const applicableTill = entityFeeModel?.applicableTill ? dayjs.utc(entityFeeModel.applicableTill).local() : null

  const formatDate = (date) => {
    return date?.format(MOMENT_FORMATS.completeDateDetailsWithMonthShortForm) || 'Not specified'
  }

  return (
    <div className="d-flex flex-column gap-1 mt-2 p-2" style={{
      backgroundColor: '#f8f9fa',
      border: '1px solid #dee2e6',
      borderRadius: '6px',
      fontSize: '14px'
    }}>
      {isInactive && (
        <div style={{ color: '#6c757d', fontWeight: 600 }}>Inactive model</div>
      )}
      <div className="d-flex align-items-center gap-2">
        <strong style={{ color: '#495057' }}>Valid:</strong>
        <span>
          {applicableFrom ? formatDate(applicableFrom) : 'Always'}
          {' → '}
          {applicableTill ? formatDate(applicableTill) : 'Ongoing'}
        </span>
      </div>
    </div>
  )
}
